import moment from 'moment';
import { createAppSlice } from '.';
import {
  getUsers,
  getUsersOfCluster,
  hasPartnerToken as hasPartnerTokenApi,
  linkBeaconToDriver as linkBeaconToDriverApi,
  linkTagToUser as linkTagToUserApi,
  linkUserToClusterByMail,
  patchUserClusterInfos,
  postPartnerToken as postPartnerTokenApi,
  retrieveMe as retrieveMeApi,
  unlinkTagFromUser as unlinkTagFromUserApi,
  unlinkUser as unlinkUserApi,
  updateOptin as updateOptinApi,
  updatePassword as updatePasswordApi,
  updateProfile as updateProfileApi,
} from '../api/api';
import { USER_CONSTANTS, UserRole, UserTimeSystem, UserUnitSystem } from '../constants';
import { SNACK_CONSTANTS } from '../constants/snackConstants';
import { BeaconDriverHistory, BeaconUUID } from './beacons';
import { ClusterId } from './clusters';
import { alerting, errorHasOccurred, snackAdded, success } from './snacks';
import { Tag, TagId } from './tags';

export type UserId = number;

type ClusterRole = {
  cluster_id: ClusterId;
  role: UserRole;
  certiphyto_number?: string;
  phonenumber?: string;
};

// the keys the API stores tag preferences under; 'device' and 'beacon' are set together from one selector
type TagPreferenceKey = 'parcel' | 'equipment' | 'user' | 'device' | 'beacon';

type TagPreferences = Partial<Record<TagPreferenceKey, TagId[]>>;

export type User = {
  id: UserId;
  firstname?: string;
  lastname?: string;
  fullname?: string;
  email?: string;
  phonenumber?: string;
  cluster_roles?: ClusterRole[];
  current_beacon_uuid?: BeaconUUID;
  unit_area: UserUnitSystem;
  unit_distance: UserUnitSystem;
  unit_time: UserTimeSystem;
  role?: UserRole;
  cluster_id?: ClusterId;
  tags?: Tag[];
  tag_ids_preference?: TagPreferences;
};

type State = Partial<User> & {
  isFetching: boolean;
  isAuthenticated: boolean;
  token?: string;
  expireDate?: string;
  isImpersonating?: boolean;
  impersonated?: boolean;
  editableDemo?: boolean;
  usersOfCluster: Record<ClusterId, User[]>;
  usersByID: Record<UserId, User>;
  partners: string[];
};

const initialState: State = {
  isFetching: false,
  isAuthenticated: false,
  usersOfCluster: {},
  usersByID: {},
  partners: [],
};

const usersSlice = createAppSlice({
  name: 'user',
  initialState,
  reducers: (create) => ({
    loginReceived: create.reducer<{
      token: string;
      expireDate: string;
      impersonated?: boolean;
      editableDemo?: boolean;
    }>((_state, { payload }) => {
      return {
        ...initialState,
        ...payload,
        isImpersonating: true,
        isAuthenticated: true,
        isFetching: false,
        impersonated: payload.impersonated,
      };
    }),
    userLinkedWithSuccess: create.reducer<{ user: User; cluster_id: ClusterId; user_id: UserId }>(
      (state, { payload }) => {
        const { cluster_id, user_id, user } = payload;
        const clusterUsers = state.usersOfCluster[cluster_id] ? [...state.usersOfCluster[cluster_id]] : [];
        const idx = clusterUsers.findIndex((u) => u.id === user_id);
        if (idx !== -1) {
          clusterUsers[idx] = user;
        } else {
          clusterUsers.push(user);
        }
        state.usersOfCluster[cluster_id] = clusterUsers;
        user.cluster_roles?.forEach((cr) => {
          if (cr.role === USER_CONSTANTS.ROLE.DRIVER || cr.role === USER_CONSTANTS.ROLE.SEASONAL_DRIVER) {
            state.usersByID[user.id] = user;
          }
        });
      },
    ),
    driverAndBeaconLinked: create.reducer<{
      id: BeaconUUID;
      driverID: UserId;
      cluster_id: ClusterId;
      user: User;
      history: BeaconDriverHistory[];
    }>((state, { payload }) => {
      const { cluster_id, user, driverID } = payload;
      const clusterUsers = state.usersOfCluster[cluster_id] ? [...state.usersOfCluster[cluster_id]] : [];
      const idx = clusterUsers.findIndex((u) => u.id === user.id);
      if (idx !== -1) clusterUsers[idx] = user;
      else clusterUsers.push(user);
      state.usersOfCluster[cluster_id] = clusterUsers;
      user.cluster_roles?.forEach((cr) => {
        if (cr.role === USER_CONSTANTS.ROLE.DRIVER || cr.role === USER_CONSTANTS.ROLE.SEASONAL_DRIVER) {
          state.usersByID[driverID] = user;
        }
      });
    }),
    userLoggedOut: create.reducer(() => {
      return initialState;
    }),
    retrieveMe: create.asyncThunk(
      async (_, { dispatch }) => {
        const me = (await alerting(() => retrieveMeApi(), { dispatch })) as User;
        return me;
      },
      {
        fulfilled: (state, { payload }) => ({ ...state, ...payload }),
      },
    ),
    updateProfile: create.asyncThunk(
      async ({ id, data }: { id: UserId; data: Partial<User> }, { dispatch }) => {
        const res = (await alerting(() => updateProfileApi(id, data), { dispatch })) as User;
        success('Admin.update_user_confirm', { dispatch });
        return res;
      },
      {
        fulfilled: (state, { payload }) => ({ ...state, ...payload }),
      },
    ),
    updateUserInfos: create.asyncThunk(
      async (
        {
          userID,
          clusterID,
          data,
        }: {
          userID: UserId;
          clusterID: ClusterId;
          data: {
            role?: UserRole;
            certiphyto_number?: string;
            cluster_phonenumber?: string;
            firstname?: string;
            lastname?: string;
            phonenumber?: string;
            email?: string;
          };
        },
        { dispatch, getState },
      ) => {
        try {
          let user: User | undefined;
          let clusterUser: { role: UserRole; certiphyto_number?: string; phonenumber?: string } | undefined;
          if (data.role || data.certiphyto_number || data.cluster_phonenumber) {
            clusterUser = (await patchUserClusterInfos(
              userID,
              clusterID,
              data.role,
              data.certiphyto_number,
              data.cluster_phonenumber,
            )) as {
              role: UserRole;
              certiphyto_number?: string;
              phonenumber?: string;
            };
          }
          if (data.firstname) {
            user = (await updateProfileApi(userID, {
              firstname: data.firstname,
              lastname: data.lastname,
              phonenumber: data.phonenumber,
              email: data.email,
            })) as User;
          } else {
            user = (getState() as { user: State }).user.usersByID[userID];
          }
          if (!user) return;
          const driverWithNewRole = {
            ...user,
            cluster_roles: clusterUser
              ? (user.cluster_roles || []).map((cr) =>
                  cr.cluster_id === clusterID
                    ? {
                        role: clusterUser!.role,
                        cluster_id: clusterID,
                        certiphyto_number: clusterUser!.certiphyto_number,
                        phonenumber: clusterUser!.phonenumber ?? cr.phonenumber,
                      }
                    : cr,
                )
              : user.cluster_roles,
            role: clusterUser ? clusterUser.role : user.cluster_roles?.find((cr) => cr.cluster_id === clusterID)?.role,
            cluster_id: clusterID,
          } as User & { cluster_id: ClusterId };
          if (!(getState() as { user: State }).user.usersOfCluster[clusterID]) {
            await dispatch(fetchUsersOfCluster(clusterID));
          }
          success('Admin.update_user_confirm', { dispatch });
          return driverWithNewRole;
        } catch (e) {
          errorHasOccurred(e, dispatch);
        }
      },
      {
        fulfilled: (state, { payload }) => {
          if (!payload) return;
          const { cluster_id, id } = payload;
          state.usersOfCluster[cluster_id] = state.usersOfCluster[cluster_id]
            ? state.usersOfCluster[cluster_id].map((u) => (u.id === id ? payload : u))
            : [payload];
          state.usersByID[id] = payload;
        },
      },
    ),
    updateOptin: create.asyncThunk(
      async ({ id, data }: { id: UserId; data: Record<string, unknown> }, { dispatch }) => {
        await alerting(() => updateOptinApi(id, data), { dispatch });
        success('Settings.notifications_saved', { dispatch });
        return data;
      },
      {
        fulfilled: (state, { payload }) => ({ ...state, ...payload }),
      },
    ),
    updatePassword: create.asyncThunk(
      async (
        {
          id,
          data,
        }: { id: UserId; data: { current_password: string; new_password: string; new_password_confirmation: string } },
        { dispatch },
      ) => {
        try {
          const user = (await updatePasswordApi(id, data)) as User;
          success('Admin.update_password_confirm', { dispatch });
          return { user, error: undefined };
        } catch (e: unknown) {
          const errorMessage = (e as { errors?: { message: string }[] })?.errors?.[0]?.message;
          dispatch(snackAdded({ type: SNACK_CONSTANTS.TYPE.ERROR, message: errorMessage ?? '', action: 'OK' }));
          return { user: undefined, error: errorMessage };
        }
      },
      {
        fulfilled: (state, { payload }) => {
          if (payload.user) return { ...state, ...payload.user };
          return state;
        },
      },
    ),
    fetchUsersOfCluster: create.asyncThunk(
      async (cluster_id: ClusterId, { dispatch }) => {
        const users = (await alerting(() => getUsersOfCluster(cluster_id), { dispatch })) as User[];
        return { users, cluster_id };
      },
      {
        pending: (state) => {
          state.isFetching = true;
        },
        fulfilled: (state, { payload }) => {
          state.usersOfCluster[payload.cluster_id] = payload.users;
        },
        settled: (state) => {
          state.isFetching = false;
        },
      },
    ),
    linkUser: create.asyncThunk(
      async (
        payload: {
          email?: string;
          clusterID: ClusterId;
          role: UserRole;
          firstname?: string;
          lastname?: string;
          phonenumber?: string;
          certiphyto_number?: string;
        },
        { dispatch },
      ) => {
        const { email, clusterID } = payload;
        const user = (await alerting(
          () => linkUserToClusterByMail(payload as Parameters<typeof linkUserToClusterByMail>[0]),
          { dispatch },
        )) as User;
        if (email) {
          success('Admin.add_user_confirm', { params: { email }, dispatch });
        } else {
          success('Admin.add_user_confirm_no_access', { dispatch });
        }
        dispatch(userLinkedWithSuccess({ user, cluster_id: clusterID, user_id: user.id }));
        return { ...user, role: payload.role };
      },
    ),
    unlinkUser: create.asyncThunk(
      async ({ clusterID, userID }: { clusterID: ClusterId; userID: UserId }, { dispatch }) => {
        await alerting(() => unlinkUserApi(clusterID, userID), { dispatch });
        success('Admin.unlink_user_successful', { dispatch });
      },
      {
        fulfilled: (
          state,
          {
            meta: {
              arg: { clusterID, userID },
            },
          },
        ) => {
          state.usersOfCluster[clusterID] = (state.usersOfCluster[clusterID] || []).filter((u) => u.id !== userID);
          delete state.usersByID[userID];
        },
      },
    ),
    linkDriver: create.asyncThunk(
      async (
        {
          beaconID,
          email,
          clusterID,
          role,
          firstname,
          lastname,
          phonenumber,
        }: {
          beaconID?: BeaconUUID;
          email?: string;
          clusterID: ClusterId;
          role: UserRole;
          firstname?: string;
          lastname?: string;
          phonenumber?: string;
        },
        { dispatch },
      ) => {
        const driver = (await alerting(
          () =>
            linkUserToClusterByMail({ email, clusterID, role, firstname, lastname, phonenumber } as Parameters<
              typeof linkUserToClusterByMail
            >[0]),
          { dispatch },
        )) as User;
        if (beaconID) {
          const history = await alerting(
            () => linkBeaconToDriverApi(beaconID, { user_id: driver.id, from_date: moment().format() }),
            { dispatch },
          );
          if (email) {
            success('Admin.add_user_confirm', { params: { email }, dispatch });
          } else {
            success('Admin.add_user_confirm_no_access', { dispatch });
          }
          dispatch(
            driverAndBeaconLinked({ id: beaconID, driverID: driver.id, cluster_id: clusterID, user: driver, history }),
          );
        } else {
          if (email) {
            success('Admin.add_user_confirm', { params: { email }, dispatch });
          } else {
            success('Admin.add_user_confirm_no_access', { dispatch });
          }
          dispatch(userLinkedWithSuccess({ user: driver, cluster_id: clusterID, user_id: driver.id }));
        }
        return driver;
      },
    ),
    fetchUsers: create.asyncThunk(
      async (_, { dispatch }) => {
        const users = (await alerting(() => getUsers(), { dispatch })) as User[];
        return users;
      },
      {
        pending: (state) => {
          state.isFetching = true;
        },
        fulfilled: (state, { payload }) => {
          state.usersByID = {};
          (payload || []).forEach((u) => {
            state.usersByID[u.id] = u;
          });
        },
        settled: (state) => {
          state.isFetching = false;
        },
      },
    ),
    postPartnerToken: create.asyncThunk(
      async ({ partnerName, data }: { partnerName: string; data: Record<string, unknown> }, { dispatch }) => {
        await alerting(() => postPartnerTokenApi(partnerName, data), { dispatch });
        return partnerName;
      },
      {
        fulfilled: (
          state,
          {
            meta: {
              arg: { partnerName },
            },
          },
        ) => {
          if (!state.partners.includes(partnerName)) {
            state.partners.push(partnerName);
          }
        },
      },
    ),
    hasPartnerToken: create.asyncThunk(
      async (partnerName: string = 'smag', { dispatch }) => {
        try {
          await hasPartnerTokenApi(partnerName);
          return partnerName;
        } catch (e: unknown) {
          const isAuthorizationError = !!(e as { errors?: { reason: string }[] })?.errors?.find(
            (err) => err.reason === 'authorizationError',
          );
          if (!isAuthorizationError) {
            errorHasOccurred(e, dispatch);
          }
          return null;
        }
      },
      {
        fulfilled: (state, { payload }) => {
          if (payload && !state.partners.includes(payload)) {
            state.partners.push(payload);
          }
        },
      },
    ),
    linkTagToUser: create.asyncThunk(
      async ({ userId, tag }: { userId: UserId; tag: Tag }, { dispatch }) => {
        await alerting(() => linkTagToUserApi(userId, tag.id), { dispatch });
      },
      {
        fulfilled: (
          state,
          {
            meta: {
              arg: { userId, tag },
            },
          },
        ) => {
          const user = state.usersByID[userId];
          if (user) user.tags = [...(user.tags || []), tag];
        },
      },
    ),
    unlinkTagFromUser: create.asyncThunk(
      async ({ userId, tagId }: { userId: UserId; tagId: TagId }, { dispatch }) => {
        await alerting(() => unlinkTagFromUserApi(userId, tagId), { dispatch });
      },
      {
        fulfilled: (
          state,
          {
            meta: {
              arg: { userId, tagId },
            },
          },
        ) => {
          const user = state.usersByID[userId];
          if (user?.tags) user.tags = user.tags.filter((t) => t.id !== tagId);
        },
      },
    ),
  }),
  selectors: {
    selectCurrentUser: (state) => state,
    selectUsersByID: (state) => state.usersByID,
    selectTagPreferences: (state) => state.tag_ids_preference,
    selectIsUserFetching: (state) => state.isFetching,
    selectUsersOfCluster: (state) => state.usersOfCluster,
    selectUserClusterRoles: (state) => state.cluster_roles,
    selectIsImpersonating: (state) => state.isImpersonating,
    selectImpersonated: (state) => state.impersonated,
    selectEditableDemo: (state) => state.editableDemo,
    selectUserEmail: (state) => state.email,
    selectUserToken: (state) => state.token,
  },
});

export const {
  selectCurrentUser,
  selectEditableDemo,
  selectImpersonated,
  selectIsImpersonating,
  selectIsUserFetching,
  selectTagPreferences,
  selectUserClusterRoles,
  selectUserEmail,
  selectUserToken,
  selectUsersByID,
  selectUsersOfCluster,
} = usersSlice.selectors;

export const {
  loginReceived,
  userLinkedWithSuccess,
  driverAndBeaconLinked,
  userLoggedOut,
  retrieveMe,
  updateProfile,
  updateUserInfos,
  updateOptin,
  updatePassword,
  fetchUsersOfCluster,
  linkUser,
  unlinkUser,
  linkDriver,
  fetchUsers,
  postPartnerToken,
  hasPartnerToken,
  linkTagToUser,
  unlinkTagFromUser,
} = usersSlice.actions;

export default usersSlice.reducer;
